"use client";
// The leaderboard's whole client state: which group, what was typed, how it is sorted and
// which page. Everything derived is computed here, so the table only renders.
import { useMemo, useState } from "react";
import type { LeaderboardRow } from "@/lib/dashboard/rows";
import {
  ALL_TAB,
  filterRows,
  pageOfHotkey,
  paginate,
  sortRows,
  statusTabs,
  type Page,
  type SortDirection,
  type SortKey,
  type StatusTab,
} from "@/lib/dashboard/table";

export const ROWS_PER_PAGE = 50;

export interface LeaderboardState {
  tabs: StatusTab[];
  tab: string;
  setTab: (tab: string) => void;
  query: string;
  setQuery: (query: string) => void;
  sortKey: SortKey;
  sortDirection: SortDirection;
  /** Same column again flips the direction; a new column starts descending. */
  toggleSort: (key: SortKey) => void;
  page: Page<LeaderboardRow>;
  setPage: (page: number) => void;
  /**
   * Brings a miner into view: clears the group and the search that could hide it and
   * turns to its page under the current sort. False when it is not on the board at all.
   */
  reveal: (hotkey: string) => boolean;
}

export function useLeaderboard(rows: LeaderboardRow[]): LeaderboardState {
  const [tab, setTabState] = useState(ALL_TAB);
  const [query, setQueryState] = useState("");
  // Rows arrive in the backend's rank order; that is the default, not a column sort.
  const [sortKey, setSortKey] = useState<SortKey>("place");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [pageNo, setPageNo] = useState(1);

  const tabs = useMemo(() => statusTabs(rows), [rows]);

  const visible = useMemo(
    () => sortRows(filterRows(rows, tab, query), sortKey, sortDirection),
    [rows, tab, query, sortKey, sortDirection],
  );

  const page = useMemo(() => paginate(visible, pageNo, ROWS_PER_PAGE), [visible, pageNo]);

  // Narrowing the list while on page 7 would otherwise land on an empty page.
  const reset = <T,>(apply: (value: T) => void) => (value: T) => {
    apply(value);
    setPageNo(1);
  };

  return {
    tabs,
    tab,
    setTab: reset(setTabState),
    query,
    setQuery: reset(setQueryState),
    sortKey,
    sortDirection,
    toggleSort: reset<SortKey>((key) => {
      if (key === sortKey) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        return;
      }
      setSortKey(key);
      setSortDirection("desc");
    }),
    page,
    setPage: setPageNo,
    reveal: (hotkey) => {
      const target = pageOfHotkey(sortRows(rows, sortKey, sortDirection), hotkey, ROWS_PER_PAGE);
      if (target === null) return false;
      setTabState(ALL_TAB);
      setQueryState("");
      setPageNo(target);
      return true;
    },
  };
}
